import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { FORM_MODE, DATA_RANGE } from '@awp/constants';
import { FormInput, FormCheckbox, FormHorizontalTextReadOnly } from '@components/Common/AwpFormInputs';
import * as PresetSlice from '@stores/graphPresetSlice';
import { defaultValidations } from './Helpers/GraphPresetValidation';
import * as PresetHelper from './Helpers/PresetHelper';
import { DataGraphSection } from './Components/DataGraphSection';
import { ErrorList } from './Components/ErrorList';
import AwpCloseCancelButtons from '../../../components/AwpCloseCancelButton';
import { setEndDateEpoch, setDataRange } from '@stores/weatherStationSlice';
import { countGraphPresetDatasets, getMaxDataRange } from '@awp/helpers/utilsReact';
import isEqual from 'lodash/isEqual';
import GraphPresetService from '@graphs/Presets/GraphPresetService';

const GraphPresetEdit = React.forwardRef((props, ref) => {
  const { setMode, mode, setPresetId, presetId, setLoading, loading } = props;

  const dispatch = useDispatch();

  const { canManagePrivate, canManagePublic } = PresetHelper.getManagePermissions(props.currentUser);

  const currentPreset = useSelector((state) => PresetSlice.getGraphPresetById(state, presetId));
  const originalPreset = useSelector((state) => PresetSlice.originalGraphPreset(state, presetId));
  console.log(currentPreset);
  const [initialDto, setInitialDto] = useState({});
  const [maxDays, setMaxDays] = useState(null);

  const validationSchema = defaultValidations({ canManagePrivate, canManagePublic });

  const crudService = new GraphPresetService(mode);
  const { readOnly, successMessage } = crudService.crud;

  useEffect(() => {
    const endOfToday = new Date();
    dispatch(setEndDateEpoch(endOfToday.getTime()));
    dispatch(setDataRange(0));
    if (currentPreset && currentPreset.GraphPresetId > 0 && !maxDays)
      setMaxDays(getMaxDataRange(countGraphPresetDatasets(currentPreset)));

    setInitialDto(crudService.mapInitialValues({ entity: originalPreset }));
  }, []);

  const handleSubmit = async ({ values, setSubmitting, validateForm, setTouched, setErrors }) => {
    let saveResult = false;

    // check for negative value weatherStationId's, those are actually deferred so reset to null
    const updatedData = {
      ...values,
      DataGraphs: values.DataGraphs.map((graph) => ({
        ...graph,
        PersistentGraphFilters: graph.PersistentGraphFilters.map((filter) => {
          const updatedFilter = { ...filter };
          if (updatedFilter.DataGraphId === null) {
            delete updatedFilter.DataGraphId;
          }

          updatedFilter.WeatherStationId = updatedFilter.WeatherStationId < 0 ? null : updatedFilter.WeatherStationId;
          return updatedFilter;
        }),
      })),
    };

    try {
      saveResult = await PresetHelper.savePreset(updatedData, dispatch);
      if (saveResult) {
        toast.success(successMessage);
      }
    } catch (error) {
      // Handle any errors if savePreset fails
      toast.error('Save failed.', { autoClose: 5000 });
    } finally {
      if (saveResult) {
        setMode(FORM_MODE.LIST);
      }
      setSubmitting(false);
    }
  };

  const handleBtnClose = () => {
    if (currentPreset.GraphPresetId < 0) {
      dispatch(PresetSlice.deletePreset({ graphPresetId: currentPreset.GraphPresetId }));
    } else {
      dispatch(PresetSlice.resetPreset({ graphPresetId: currentPreset.GraphPresetId }));
    }
    setMode(FORM_MODE.LIST);
  };

  const Buttons = ({ props }) => {
    if (mode !== FORM_MODE.VIEW) {
      const myIsPristine = crudService.mapInitialValues({ entity: props.values });

      return (
        <AwpCloseCancelButtons
          props={props}
          isPristine={
            (isEqual(myIsPristine, initialDto) && isEqual(myIsPristine.DataGraphs, initialDto.DataGraphs)) ||
            props.isSubmitting
          }
          hasAccess={true}
          onSaveSuccess={() => {
            handleSubmit(props);
          }}
          onConfirmedCancel={() => {
            handleBtnClose();
          }}
        />
      );
    } else {
      return (
        <>
          {(canManagePublic && props.values.IsPublic) || (!props.values.IsPublic && canManagePrivate) ? (
            <Button type="button" className="mr-2" variant="outline-primary" onClick={() => setMode(FORM_MODE.EDIT)}>
              Edit
            </Button>
          ) : null}
          <Button className="mr-2" variant="outline-primary" onClick={() => handleBtnClose(props)}>
            Close
          </Button>
        </>
      );
    }
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {/* {props.loading || presetData === {} ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : ( */}
      <>
        <Formik
          enableReinitialize={true}
          validateOnMount
          validateOnBlur
          validateOnChange
          initialTouched={{ Name: true }}
          // initialTouched={Object.keys(presetData)
          //   .map((o) => o)
          //   .reduce((acc, item, idx) => {
          //     return { ...acc, [item]: true };
          //   }, {})}
          initialValues={{
            ...originalPreset,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <>
              <div className="d-flex">{crudService.crud.getHeader()}</div>
              <Form>
                <Card border="light" className="page-card" style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}>
                  <Card.Body>
                    {mode !== FORM_MODE.CREATE && maxDays && maxDays > DATA_RANGE.MAXIMUM && (
                      <Row>
                        <Col lg={6} md={8} sm={10}>
                          {maxDays && <FormHorizontalTextReadOnly label="Maximum days:" textValue={maxDays} />}
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col lg={6} md={8} sm={10}>
                        {readOnly ? (
                          <div className="flex-grow-1">
                            <h2>{currentPreset.Name}</h2>
                          </div>
                        ) : (
                          <FormInput
                            label="Preset Name (required)"
                            instruction="50 Characters max."
                            readOnly={readOnly}
                            name="Name"
                            id="Name"
                            placeholder="Enter Preset Name"
                            onChange={(e) => {
                              dispatch(
                                PresetSlice.setPresetName({
                                  graphPresetId: currentPreset.GraphPresetId,
                                  newPresetName: e.target.value,
                                }),
                              );
                              props.setFieldTouched('Name', true);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                // Update the graph name when the Enter key is pressed
                                dispatch(
                                  PresetSlice.setPresetName({
                                    graphPresetId: currentPreset.GraphPresetId,
                                    newPresetName: e.target.value,
                                  }),
                                );
                                props.setFieldTouched('Name', true);
                              }
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                    <FormCheckbox
                      label="Public"
                      readOnly={readOnly || !canManagePublic} // Disable if readOnly or if the user can't manage public presets
                      name="IsPublic"
                      id="IsPublic"
                    />
                    <DataGraphSection ref={ref} data={currentPreset} readOnly={readOnly} formik={props} />
                    <Buttons title=" " props={props} />
                    <Row>
                      <ErrorList formik={props} data={currentPreset} />
                    </Row>
                  </Card.Body>
                </Card>
              </Form>
            </>
          )}
        </Formik>
      </>
    </>
  );
});

GraphPresetEdit.displayName = 'GraphPresetEdit';

export default GraphPresetEdit;
